// Types de l'API « chantiers » (carnet de chantier, incrément 2). Aucun montant : libellés, quantités et heures seulement.
export type Lieu = 'site' | 'bureau';

export interface Etape {
  id: string; position: number; label: string; detail: string | null; section: string | null; reference: string | null;
  qty_planned: string; unit: string; hours_per_unit: string | null; hours_planned: string | null; photo_required: boolean;
  origin: 'devis' | 'hors_devis' | 'revision'; retiree: { le: string; motif: string | null } | null;
  /** Incrément 7 : étape ajoutée par le report de la révision N du devis. */
  ajoutee_par_revision?: number | null;
  /** Incrément 7 : ligne retirée du devis par la révision N (faite : l'étape reste, à arbitrer). */
  retiree_du_devis?: { rev_no: number; faite: boolean } | null;
}

export interface APoser { label: string; manufacturer: string | null; reference: string | null; qty: string; unit: string }

export interface Poste {
  id: string; position: number; label: string; lot_label: string | null; place: Lieu; crew_min: number | null; origin: string;
  location: { id: string; label: string } | null; equipment: { id: string; label: string } | null; ouvrage: { id: string; code: string } | null;
  a_poser: APoser[]; retire: { le: string; motif: string | null } | null;
  temps: { heures: string; a_renseigner: number; nb_etapes: number };
  etapes: Etape[];
}

export type Source = 'chantier' | 'entreprise' | 'defaut' | 'absente';
export type Choix = { mode: 'effectif'; personnes: number } | { mode: 'jours'; jours: number };

export type Effectif =
  | { disponible: false; raison: string; max_utile: number }
  | {
    disponible: true; capacite_jour: string; jours_personne: string; max_utile: number; max_utile_non_precise: boolean; base_incomplete: boolean;
    lignes: { personnes: number; jours: number }[];
    retenu: null | { mode: 'effectif' | 'jours'; personnes: number; jours: number; alerte: string | null };
  };

export interface VueChantier {
  avertissements_repartition?: string[];
  chantier: {
    id: string; reference: string | null; title: string; status: string; demo: boolean; version: number;
    site: { id: string; nom: string; adresse: string | null; ville: string | null } | null;
    devis: null | { id: string; number: string; title: string; variante: string; revision_id: string; rev_no: number; acceptee_le: string | null;
      dossier: { id: string; reference: string; title: string } };
    sans_devis: boolean;
    responsable: { id: string; nom: string; actif: boolean } | null;
    planned_start: string | null; planned_end: string | null; site_notes: string | null;
    cree_le: string; lance_le: string | null; lance_par: string | null;
  };
  hypotheses: {
    heures_sur_site_par_jour: { valeur: string | null; source: Source };
    rendement_pct: { valeur: string; source: Source };
    choix: Choix | null;
    lieu_programmation_propose: { valeur: Lieu; precise: boolean } | null;
  };
  postes: Poste[];
  temps: {
    site: string; bureau: string; etapes_a_renseigner: number; incomplet: boolean; nb_etapes: number;
    deplacements: { heures: string; lignes: number; a_renseigner: number } | null;
  };
  effectif: Effectif;
  lignes_non_reprises: { label: string; section: string; motif: string; heures: string | null }[];
  revision_plus_recente: { revision_id: string; rev_no: number; statut: string; acceptee_le: string | null } | null;
  /** Incrément 7 : révision acceptée à reporter (seulement pour qui prépare les chantiers), avec le résumé de l'écart. */
  revision_a_reporter?: { revision_id: string; rev_no: number; acceptee_le: string; depuis_rev_no: number; resume: ResumeEcart } | null;
  dernier_report?: { rev_no: number; le: string; par: string | null } | null;
  /** Réception des travaux (15/10/2026) : point de départ des durées de conservation. */
  reception: VueReception;
  droits: { preparer: boolean; receptionner: boolean };
}

export interface Reception {
  id: string; date: string; avec_reserves: boolean; note: string | null; motif: string | null; remplace: string | null;
  piece: { nom: string; mime: string; taille: number | null } | null; par: string; le: string;
}
export interface VueReception {
  actuelle: Reception | null;
  historique: Reception[];
  /** Échéances de conservation (null : sans réception, ou démonstration — jamais purgée). */
  echeances: { historique: string | null; temps: string | null };
  traite: { historique_le: string | null; temps_le: string | null; temps_nominatif: boolean };
  aujourdhui: string;
  /** Premier jour réel du chantier : la réception ne peut pas être antérieure. */
  premier_jour: string;
  /** Fin du délai de garde (30 jours après la dernière déclaration ou correction) : aucune purge de ce chantier avant. */
  garde_jusquau: string | null;
  possible: boolean;
}

// ─── Nouvelle révision du devis après le lancement (incrément 7). Aucun montant. ───
export type SorteEcart = 'ajoutee' | 'retiree' | 'modifiee' | 'inchangee';
export type Changement = 'quantite' | 'heures' | 'libelle' | 'unite';
export interface ResumeEcart { ajoutees: number; retirees: number; quantites: number; heures: number; libelles: number; a_poser: number; inchangees: number; reportables: number }
export interface ValeursLigne { label: string; section: string; reference: string | null; quantite: string; unite: string; heures_par_unite: string | null; heures: string | null }
export interface ConsequenceEtape {
  etape_id: string; label: string; statut: string; qty_done: string; qty_planned: string; unit: string;
  quoi: 'retiree' | 'reste_marquee' | 'modifiee' | 'complement' | 'rien';
  nouvelles?: { qty_planned?: string; hours_per_unit?: string | null; label?: string; unit?: string; quote_line_key?: string };
  qty?: string; pourquoi?: string;
}
export interface PosteCible { id: string | null; label: string }
export interface ItemEcart {
  cle: string; sorte: SorteEcart; ancienne_cle: string | null; nouvelle_cle: string | null; rapprochee: boolean;
  avant: ValeursLigne | null; apres: ValeursLigne | null; changements: Changement[]; etapes: ConsequenceEtape[];
  ajout: null | { etape: { label: string; qty_planned: string; unit: string; hours_planned: string | null }; poste: PosteCible };
  a_poser: null | { quoi: 'ajout' | 'retrait' | 'quantite'; poste: PosteCible | null; label: string; reference: string | null; qty_avant: string | null; qty_apres: string | null; unit: string };
  candidats: string[]; reportable: boolean; note: string | null;
}
export interface CompteRenduReport { id: string; rev_no: number; le: string; par: string | null; bilan: Partial<BilanReport>; choix?: string[] }
export interface BilanReport { etapes_ajoutees: number; etapes_retirees: number; etapes_marquees: number; etapes_modifiees: number; complements: number;
  postes_crees: number; a_poser: number; non_reportes: number }
export interface EcartRevision {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean; version: number };
  revision: { id: string; rev_no: number; acceptee_le: string } | null;
  depuis?: { id: string; rev_no: number };
  ecart?: { items: ItemEcart[]; sans_effet: { cle: string; sorte: SorteEcart; avant: ValeursLigne | null; apres: ValeursLigne | null; motif: string }[]; resume: ResumeEcart };
  postes?: { id: string; label: string }[];
  rapprochements?: { id: string; ancienne_cle: string; nouvelle_cle: string; par: string | null; le: string }[];
  deja: CompteRenduReport | null;
  dernier_report: CompteRenduReport | null;
  motif: string | null;
}

export interface ChantierResume {
  id: string; reference: string | null; title: string; status: string; demo: boolean; updated_at: string; launched_at: string | null;
  planned_start: string | null; site_name: string; devis_number: string | null; responsable: string | null; nb_postes: number; nb_etapes: number;
}

// ─── Planning et « Ma journée » (incrément 3). Toujours aucun montant. ───
export type Creneau = 'journee' | 'matin' | 'apres_midi';
export interface PersonnePlanning { id: string; nom: string; actif: boolean; telephone?: string }

export interface AffectationChantier {
  id: string; jour: string; personne: PersonnePlanning; poste: { id: string; label: string; lieu?: string | null } | null; etape_ids: string[];
  creneau: Creneau; heures: string | null; note: string | null;
  retiree: { le: string; par: string | null; motif: string | null } | null;
}

export interface PlanningChantier {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean; responsable_id: string | null };
  du: string; au: string; aujourdhui: string; jours: string[];
  postes: { id: string; label: string; lieu?: string | null; crew_min: number | null; place: Lieu; retire: boolean; etapes: { id: string; label: string; hours_planned: string | null; statut: string }[] }[];
  affectations: AffectationChantier[];
  alertes: { type: 'conflit' | 'effectif'; jour: string; message: string; personne_id?: string; poste_id?: string }[];
  couverture: null | {
    heures_affectees_a_venir: string; affectations_a_venir: number; affectations_sans_heures: number;
    heures_prevues_restantes: string; etapes_sans_temps: number; manque: string;
  };
  heures_par_jour: string | null;
  droits: { affecter: boolean; voir_tout: boolean; motif_lecture_seule: string | null };
}

export interface PlanningGlobal {
  du: string; au: string; aujourdhui: string; jours: string[];
  chantiers: { id: string; reference: string | null; title: string; status: string; demo: boolean; site: string; responsable: string | null }[];
  personnes: { id: string; nom: string; actif: boolean }[];
  affectations: { id: string; chantier_id: string; jour: string; personne_id: string; creneau: Creneau; heures: string | null; note: string | null;
    poste: { id: string; label: string } | null }[];
  conflits: { personne_id: string; jour: string; message: string }[];
  sous_effectif: { chantier_id: string; poste_id: string; jour: string; message: string }[];
  droits: { affecter_tout: boolean };
}

export interface EtapeJour {
  id: string; position: number; label: string; detail: string | null; qty_planned: string; unit: string; hours_planned: string | null;
  photo_required: boolean; statut: string; qty_done: string;
  /** Incrément 4 : coches valables de l'étape (la première fait foi). */
  coches: CocheEtape[];
  quantite_depassee: boolean;
  /** Incrément 5 : photos (non retirées) déjà prises pour cette étape. */
  photos: number;
}

// ─── Journal (incrément 4) : coches, consignes de relève, temps passé, avancement, page du jour. Aucun montant. ───
export interface CocheEtape { id: string; sorte: 'fait' | 'partiel'; quantite: string | null; par: string; avec: string[]; a: string; moi: boolean; hors_poste: boolean }
export interface ReleveJour { id: string; texte: string; par: string; le: string; jour: string; moi: boolean }

export interface MesuresAvancement {
  etapes: { faites: number; total: number; en_cours: number; faites_sans_temps: number; sans_temps: number; faites_retirees: number;
    doubles_coches: number; quantites_depassees: number };
  heures_prevues: { faites: string; prevues: string; reste: string };
  heures_passees: { total: string | null; declarations: number };
}

export interface Avancement {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean };
  mesures: MesuresAvancement;
  postes: {
    id: string; label: string; place: Lieu; retire: boolean; mesures: MesuresAvancement;
    etapes: { id: string; label: string; statut: string; qty_planned: string; qty_done: string; unit: string; hours_planned: string | null; retiree: boolean;
      faite_le: string | null; par: string[]; coches: number; double_coche: boolean; quantite_depassee: boolean;
      ajoutee_par_revision?: number | null; retiree_du_devis?: number | null; historique?: HistoriqueValeur[] }[];
  }[];
  temps_non_reparti: string;
  signalements: { hors_poste: number; heure_incoherente: number } | null;
  /** Incrément 7 : étapes faites dont la ligne a été retirée du devis (ou retirées après coche) — à arbitrer. */
  a_arbitrer?: number;
  jours: { jour: string; coches: number; annulations: number; consignes: number; temps: number; affectes: number; terrain: number }[];
  aujourdhui: string;
  reglages?: { photos: boolean; temps: boolean };
  droits: { complet: boolean };
}

/** Historique des valeurs prévues d'une étape (reports de révision, corrections après le lancement), en ajout seul. */
export interface HistoriqueValeur { champ: string; libelle: string; avant: string | null; apres: string | null; motif: string | null; par: string; le: string; rev_no: number | null }

export interface PersonneJournal { id: string; nom: string; actif: boolean }
export interface PageJour {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean };
  jour: string; aujourdhui: string; travaille: boolean; precedent: string | null; suivant: string | null;
  affectes: (PersonnePlanning & { poste: string | null; creneau: Creneau; heures_prevues: string | null })[] | null;
  coches: {
    id: string; sorte: 'fait' | 'partiel'; quantite: string | null; unite: string | null; etape: { id: string; label: string; poste: string | null };
    declaree_par: PersonneJournal; realisee_par: PersonneJournal[]; a: string; recue_a: string; note: string | null;
    hors_poste: boolean; heure_incoherente: boolean; aussi?: boolean; sans_photo: string | null;
    annulee: { par: string; le: string; motif: string | null } | null; annulable: boolean;
  }[];
  annulations_du_jour: { id: string; par: string; le: string; motif: string | null; coche: { id: string; jour: string; etape: string; par: string } }[];
  consignes: { id: string; poste: string | null; texte: string | null; par: PersonneJournal; le: string;
    retiree: { par: string; le: string; motif: string | null } | null; retirable: boolean }[];
  temps: { id: string; personne: PersonneJournal; poste: string | null; poste_id: string | null; heures: string; declare_par: string; le: string; motif: string | null;
    historique: { heures: string; par: string; le: string; motif: string | null }[] }[];
  temps_total: string;
  temps_partiel: boolean;
  terrain: TerrainJour;
  /** Réglages de la société (confidentialité des salariés). */
  reglages?: { photos: boolean; temps: boolean };
  droits: { complet: boolean; corriger: boolean; pointer: boolean; affecte: boolean; saisir: boolean; temps?: boolean; photos?: boolean };
}

export interface AffectationJour {
  id: string; jour: string; creneau: Creneau; heures: string | null; note: string | null;
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean; consignes_acces: string | null };
  site: { id: string; nom: string; adresse: string | null; ville: string | null };
  poste: { id: string; label: string; lieu: string | null; lieu_id: string | null; crew_min: number | null; a_poser: APoser[] } | null;
  postes_du_chantier: { id: string; label: string; nb_etapes: number }[];
  etapes_choisies: boolean;
  etapes: EtapeJour[];
  coequipiers: (PersonnePlanning & { poste: string | null; creneau: Creneau })[];
  responsable: PersonnePlanning | null;
  vous_etes_responsable: boolean;
  peut_cocher: boolean;
  releve: ReleveJour | null;
  releve_chantier: ReleveJour | null;
  temps_ici: boolean;
  mon_temps: { heures: string; le: string } | null;
  reserves_ouvertes: ReserveOuverte[];
}

export interface MaJournee { aujourdhui: string; jusqu_au: string; jours: { jour: string; affectations: AffectationJour[] }[];
  /** Réglages de la société (confidentialité des salariés) : photos et déclaration du temps (le serveur refuse si désactivées). */
  reglages?: { photos: boolean; temps: boolean } }

// ─── Terrain enrichi (incrément 5) : réserves et aléas, mesures, pièces posées, photos. Aucun montant. ───
export type NatureReserve = 'alea' | 'reserve_client' | 'reserve_interne';
export const NATURES_RESERVE: Record<NatureReserve, string> = { alea: 'Aléa', reserve_client: 'Réserve du client', reserve_interne: 'Réserve interne' };

/** Réserve ouverte vue dans « Ma journée ». */
export interface ReserveOuverte { id: string; nature: NatureReserve; nature_libelle: string; texte: string; poste_id: string | null; poste: string | null;
  etape: string | null; par: string; le: string; moi: boolean }

export interface Reserve {
  id: string; nature: NatureReserve; nature_libelle: string; texte: string; jour: string; le: string; par: PersonneJournal; moi: boolean; heure_incoherente: boolean;
  poste: { id: string; label: string } | null; etape: { id: string; label: string } | null; equipement: { id: string; label: string } | null;
  lieu: { id: string; label: string } | null;
  statut: 'ouverte' | 'levee';
  levee: { par: PersonneJournal; le: string; jour: string; note: string; sans_objet: boolean } | null;
  photos: { id: string }[];
  levable: boolean;
}
export interface ListeReserves {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean };
  ouvertes: Reserve[]; levees: Reserve[];
  droits: { ouvrir: boolean; corriger: boolean; encadrant: boolean };
  aujourdhui: string;
}

export interface PiecePosee {
  id: string; jour: string; le: string; label: string; reference: string | null; fabricant: string | null; numero_serie: string | null;
  adresse_reseau: string | null; note: string | null; installe_le: string;
  lieu: { id: string; label: string } | null; poste: { id: string; label: string } | null; etape: { id: string; label: string } | null;
  equipement: { id: string; label: string; archive: boolean }; cree: boolean; remplace: { id: string; label: string } | null;
  par: PersonneJournal; photos: { id: string }[];
}
export interface ListePieces {
  chantier: { id: string; reference: string | null; title: string; status: string; demo: boolean };
  pieces: PiecePosee[];
  droits: { poser: boolean; encadrant: boolean };
  aujourdhui: string;
}

export interface TerrainJour {
  reserves_ouvertes: Reserve[];
  reserves_levees: Reserve[];
  mesures: { id: string; point: string; valeur: string; unite: string; poste: string | null; etape: string | null; equipement: { id: string; label: string } | null;
    par: string; moi: boolean; le: string; retiree: { par: string; le: string; motif: string | null } | null; retirable: boolean }[];
  pieces: PiecePosee[];
  photos: { id: string; par: string; moi: boolean; le: string; rattachement: string; retirable: boolean }[];
  /** Pour les volets de saisie (le jour même seulement) : postes et étapes non retirés, liste « À poser ». */
  structure: PosteSaisie[] | null;
}

/** Ce qu'un volet de saisie doit connaître du chantier : ses postes, leurs étapes, leur lieu et leur liste « À poser ». */
export interface PosteSaisie { id: string; label: string; lieu_id: string | null; a_poser: APoser[]; etapes: { id: string; label: string; photo_required: boolean }[] }
export interface CadreSaisie { chantierId: string; postes: PosteSaisie[]; posteId: string | null;
  /** « CHA-2026-0001 — Titre » : nomme le chantier dans l'écran « Envois » (saisie gardée sans réseau). */
  libelle?: string }
